import React from "react";
import { useTranslation } from "react-i18next";

/**
 * A stylised mockup of the product's booking UI, drawn entirely in CSS.
 *
 * This replaces the stock photograph the template shipped with: it carries no
 * licensing exposure, weighs nothing over the wire, stays sharp on any DPI,
 * and actually shows what EasyPlanner does.
 *
 * Purely decorative — hidden from assistive tech, which reads the surrounding
 * copy instead.
 */

const SLOTS = [
  { time: "09:00", state: "booked", who: "Maria P." },
  { time: "10:30", state: "booked", who: "Andrei D." },
  { time: "12:00", state: "free" },
  { time: "13:30", state: "booked", who: "Elena V." },
  { time: "15:00", state: "free" },
];

const DAYS = ["M", "T", "W", "T", "F", "S", "S"];

const BookingPreview = () => {
  const { t } = useTranslation();

  return (
    <div className="relative mx-auto w-full max-w-md" aria-hidden="true">
      {/* Glow behind the card */}
      <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-brand-200/50 via-brand-100/30 to-transparent blur-2xl" />

      {/* Main card */}
      <div className="relative rounded-2xl border border-ink-200/80 bg-white p-5 shadow-card-hover">
        {/* Title bar */}
        <div className="flex items-center justify-between border-b border-ink-100 pb-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex size-8 items-center justify-center rounded-lg bg-brand-600 text-white">
              <i className="bx bx-calendar-check text-lg" />
            </span>
            <div>
              <div className="text-sm font-semibold text-ink-900">
                {t("About.preview.title", "Today's schedule")}
              </div>
              <div className="text-[11px] text-ink-400">
                {t("About.preview.subtitle", "Snip & Style · Bucuresti")}
              </div>
            </div>
          </div>
          <div className="flex gap-1">
            <span className="size-2 rounded-full bg-ink-200" />
            <span className="size-2 rounded-full bg-ink-200" />
            <span className="size-2 rounded-full bg-brand-500" />
          </div>
        </div>

        {/* Week strip */}
        <div className="mt-4 grid grid-cols-7 gap-1">
          {DAYS.map((d, i) => (
            <div key={i} className="text-center">
              <div className="text-[10px] font-medium text-ink-400">{d}</div>
              <div
                className={`mt-1 flex h-7 items-center justify-center rounded-md text-[11px] font-semibold ${
                  i === 2
                    ? "bg-brand-600 text-white"
                    : i > 4
                      ? "text-ink-300"
                      : "bg-ink-50 text-ink-600"
                }`}
              >
                {12 + i}
              </div>
            </div>
          ))}
        </div>

        {/* Slot list */}
        <div className="mt-4 space-y-2">
          {SLOTS.map((slot) => (
            <div
              key={slot.time}
              className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${
                slot.state === "booked"
                  ? "border-ink-100 bg-ink-50/60"
                  : "border-dashed border-brand-300 bg-brand-50/50"
              }`}
            >
              <span className="w-11 shrink-0 text-[11px] font-semibold tabular-nums text-ink-500">
                {slot.time}
              </span>

              {slot.state === "booked" ? (
                <>
                  <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700">
                    {slot.who.charAt(0)}
                  </span>
                  <span className="flex-1 truncate text-xs font-medium text-ink-700">
                    {slot.who}
                  </span>
                  <span className="rounded-full bg-brand-600/10 px-2 py-0.5 text-[10px] font-semibold text-brand-700">
                    {t("About.preview.confirmed", "Confirmed")}
                  </span>
                </>
              ) : (
                <span className="flex-1 text-xs font-medium text-brand-700">
                  {t("About.preview.available", "Available")}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Floating "new booking" chip */}
      <div className="absolute -bottom-5 -right-2 flex items-center gap-2 rounded-xl border border-ink-200/80 bg-white px-3.5 py-2.5 shadow-card-hover sm:-right-6">
        <span className="inline-flex size-7 items-center justify-center rounded-full bg-brand-600 text-white">
          <i className="bx bx-check text-base" />
        </span>
        <div className="leading-tight">
          <div className="text-[11px] font-semibold text-ink-900">
            {t("About.preview.newBooking", "New booking")}
          </div>
          <div className="text-[10px] text-ink-400">
            {t("About.preview.justNow", "just now")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPreview;
